import { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";
import { db } from "../../../backend/firebaseConfig";
import Sidebar from "../../DesignSystem/Sidebar";
import Breadcrumb from "../../DesignSystem/Breadcrumb/Breadcrumb";
import PrimaryButton from "../../DesignSystem/Buttons/PrimaryButton";
import SecondaryButton from "../../DesignSystem/Buttons/SecondaryButton";
import OutlinedStatusButton from "../../DesignSystem/Buttons/OutlinedStatusButtons";
import OptionList from "../../DesignSystem/SelectionControls/OptionList";
import Stepper from "../../DesignSystem/Stepper/Stepper";
import { subscribeAdviserFullDetailsByEmailOrId } from "../../../context/Adviser/Utils/AdviserLiveDetails";
import "../../../css/Recruitment/ContractDrafting.css";
import { PDFViewer } from "@react-pdf/renderer";
import LtdCompanyContract from "./contractTemplates/drafts/LtdCompany/LtdCompanyContractPDF";
import RiContractPDF from "./contractTemplates/drafts/RIContract/RIContractPDF";
import LtdCompanyDetails from "./contractDetails/LtdCompanyDetails";
import PartnershipDetails from "./contractDetails/PartnershipDetails";
import SoleTraderDetails from "./contractDetails/SoleTraderDetails";
import TradingStyleDetails from "./contractDetails/TradingStyleDetails";
import RiContractDetails from "./contractDetails/RiContractDetails";
import { buildContractDetails } from "./contractDetails/contractDetailsDefaults";

const CONTRACT_TYPES = [
  "Ltd Company",
  "Partnership",
  "Sole Trader",
  "Trading Style",
  "RI Contract",
];
const STEPS = ["Contract", "Details & Approval", "Review"];
const CONTRACT_DETAIL_COMPONENTS = {
  "Ltd Company": LtdCompanyDetails,
  Partnership: PartnershipDetails,
  "Sole Trader": SoleTraderDetails,
  "Trading Style": TradingStyleDetails,
  "RI Contract": RiContractDetails,
};

const ContractDraftingPage = () => {
  const { adviserId } = useParams();
  const [recruitmentData, setRecruitmentData] = useState({});
  const [personalDetails, setPersonalDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeStep, setActiveStep] = useState(0);
  const [contractType, setContractType] = useState("Ltd Company");
  const [directoryDetails, setDirectoryDetails] = useState({});
  const [contractDetailsByType, setContractDetailsByType] = useState({});

  useEffect(() => {
    let received = 0;
    const loaded = () => {
      received += 1;
      if (received === 2) setLoading(false);
    };
    const failed = (snapshotError) => {
      setError(
        snapshotError?.message ||
          "The contract drafting record could not be loaded.",
      );
      setLoading(false);
    };
    const unsubscribeRecruitment = onSnapshot(
      doc(db, "Recruitment", adviserId),
      (snapshot) => {
        if (!snapshot.exists()) {
          setError("This recruitment record could not be found.");
          setLoading(false);
          return;
        }
        setRecruitmentData(snapshot.data() || {});
        loaded();
      },
      failed,
    );
    const unsubscribePersonal = onSnapshot(
      doc(db, "Recruitment", adviserId, "Personal Details", "Data"),
      (snapshot) => {
        setPersonalDetails(snapshot.exists() ? snapshot.data() : {});
        loaded();
      },
      failed,
    );
    return () => {
      unsubscribeRecruitment();
      unsubscribePersonal();
    };
  }, [adviserId]);

  const applicant = useMemo(
    () => ({ ...recruitmentData, ...personalDetails }),
    [personalDetails, recruitmentData],
  );
  const applicantName =
    applicant.displayName ||
    applicant.name ||
    [applicant.firstName, applicant.lastName].filter(Boolean).join(" ") ||
    "Applicant";
  const email =
    applicant.email || applicant.emailAddress || applicant.personalEmail || "";

  useEffect(() => {
    const adviserKey = email || adviserId;
    if (!adviserKey) return () => {};
    return subscribeAdviserFullDetailsByEmailOrId(adviserKey, (value) => {
      setDirectoryDetails(value || {});
    });
  }, [adviserId, email]);

  const sourceContractDetails = useMemo(
    () =>
      buildContractDetails({
        contractType,
        applicantName,
        directoryDetails,
      }),
    [applicantName, contractType, directoryDetails],
  );
  const contractDetails = useMemo(() => {
    const saved = contractDetailsByType[contractType];
    if (!saved) return sourceContractDetails;
    return {
      ...sourceContractDetails,
      ...saved,
      rates: { ...sourceContractDetails.rates, ...(saved.rates || {}) },
    };
  }, [contractDetailsByType, contractType, sourceContractDetails]);
  const updateContractDetails = (value) =>
    setContractDetailsByType((current) => ({
      ...current,
      [contractType]: value,
    }));
  const goForward = () => setActiveStep((step) => Math.min(step + 1, 2));
  const goBack = () => setActiveStep((step) => Math.max(step - 1, 0));
  const renderStep = () => {
    if (activeStep === 0)
      return (
        <OptionList
          className="contract-type-options"
          label="Contract Type"
          name="contractType"
          onChange={setContractType}
          options={CONTRACT_TYPES}
          value={contractType}
        />
      );
    if (activeStep === 1) {
      const DetailsComponent = CONTRACT_DETAIL_COMPONENTS[contractType];
      return (
        <DetailsComponent
          values={contractDetails}
          onChange={updateContractDetails}
        />
      );
    }
    return (
      <div className="column gap-m">
        {/* <dl>
          <div>
            <dt>Applicant</dt>
            <dd>{applicantName}</dd>
          </div>
          <div>
            <dt>Contract type</dt>
            <dd>{contractType}</dd>
          </div>
          <div>
            <dt>Start date</dt>
            <dd>{details.startDate || "Not set"}</dd>
          </div>
          <div>
            <dt>Approval confirmed</dt>
            <dd>{details.approved ? "Yes" : "No"}</dd>
          </div>
        </dl> */}
      </div>
    );
  };

  return (
    <div className="main-content">
      <Helmet>
        <title>Contract Drafting - {applicantName}</title>
      </Helmet>
      <Sidebar isSetExpanded />
      <main className="Main-Container column gap-m">
        <Breadcrumb
          items={[
            { label: "Recruitment Applications", to: "/Applications" },
            { label: "Contract Drafting" },
          ]}
        />
        <header className="row space-between">
          <h3 className="FP_H3">Contract</h3>

          <OutlinedStatusButton color="blue" label="Drafting" />
        </header>
        <section className="grid2 gap-m flex-1">
          <section className="column gap-m p-s bg-white all-radius-s contract-drafting-panel contract-details-panel">
            <Stepper
              activeStep={activeStep}
              onStepChange={setActiveStep}
              steps={STEPS}
            />
            <div className="flex-1">{renderStep()}</div>

            <footer className="row-wrap space-between gap-m">
              {activeStep === 0 ? (
                <SecondaryButton label="Skip Template" onClick={goForward} />
              ) : (
                <SecondaryButton label="Back" onClick={goBack} />
              )}
              <PrimaryButton
                disabled={activeStep === 2}
                label={
                  activeStep === 1
                    ? "Review"
                    : activeStep === 2
                      ? "Ready for renderer"
                      : "Next"
                }
                onClick={goForward}
                type="button"
              />
            </footer>
          </section>
          <section className="column gap-m p-s bg-gray-1 all-radius-s contract-drafting-panel contract-preview-panel">
            <header className="row space-between">
              <div className="FP_Subtitle_Semibold text-primary-1">
                Contract Preview
              </div>
            </header>
            <div className="column gap-m contract-preview-body">
              <article className="column bg-white gap-m contract-preview-page">
                {contractType === "Ltd Company" ? (
                  <PDFViewer
                    className="contract-pdf-viewer"
                    showToolbar={true}
                    title={`Ltd Company Contract - ${applicantName}`}
                  >
                    <LtdCompanyContract contractDetails={contractDetails} />
                  </PDFViewer>
                ) : contractType === "RI Contract" ? (
                  <PDFViewer
                    className="contract-pdf-viewer"
                    showToolbar={true}
                    title={`RI Contract - ${applicantName}`}
                  >
                    <RiContractPDF contractDetails={contractDetails} />
                  </PDFViewer>
                ) : (
                  <div className="column-center flex-1 FP_Content text-grey">
                    Select Ltd Company to preview the available contract.
                  </div>
                )}
              </article>
            </div>
          </section>
        </section>
      </main>
    </div>
  );
};

export default ContractDraftingPage;
